import type { SessionStatus } from "@mux/sdk/v2/client"

const idle = { type: "idle" as const }

export function createSessionAbortStatusGuard(input?: {
  timeoutMs?: number
  onTimeout?: (sessionID: string) => void
}) {
  const pending = new Map<string, ReturnType<typeof setTimeout>>()
  const timeoutMs = input?.timeoutMs ?? 15_000

  const end = (sessionID: string) => {
    const timer = pending.get(sessionID)
    if (timer === undefined) return
    clearTimeout(timer)
    pending.delete(sessionID)
  }

  return {
    begin(sessionID: string) {
      end(sessionID)
      const timer = setTimeout(() => {
        pending.delete(sessionID)
        input?.onTimeout?.(sessionID)
      }, timeoutMs)
      pending.set(sessionID, timer)
    },
    settle(sessionID: string, status: SessionStatus) {
      if (!pending.has(sessionID)) return
      if (status.type === "idle") return
      end(sessionID)
    },
    active(sessionID: string) {
      return pending.has(sessionID)
    },
    accept(sessionID: string, status: SessionStatus) {
      if (!pending.has(sessionID)) return true
      if (status.type !== "idle") return false
      end(sessionID)
      return true
    },
    overlay(statuses: Record<string, SessionStatus>) {
      // A pending abort owns the status until an authoritative idle arrives.
      // Snapshots are refreshed on visibility change, reconnect, and the guard's
      // own timeout, so letting a stale busy through here flips the session back
      // to running and makes the transcript tail flash.
      if (pending.size === 0) return statuses
      const next = { ...statuses }
      for (const sessionID of pending.keys()) next[sessionID] = idle
      return next
    },
    dispose() {
      for (const timer of pending.values()) clearTimeout(timer)
      pending.clear()
    },
  }
}
